import { SimulationConfig, MorphData } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { ShapeUtils } from '@/logic/physics/ShapeUtils';

export const SpectrumRenderer = {
  render: (
    ctx: CanvasRenderingContext2D,
    boosts: Float32Array,
    config: SimulationConfig,
    globalHue: number,
    morph?: MorphData
  ) => {
    if (!config.rhythmicSpectrumEnabled) return;
    
    // Exclude certain modes as per user requirement
    if (config.mode === 'ESCAPE' || config.mode === 'SPACE_SPIRAL' || config.mode === 'RHYTHMIC_ESCAPE') return;

    const barCount = config.rhythmicSpectrumBarCount || 128;
    const maxHeight = config.rhythmicSpectrumMaxHeight || 100;
    const minHeight = config.rhythmicSpectrumMinHeight || 0;
    const barWidth = config.rhythmicSpectrumBarWidth || 4;
    
    const radius = config.containerRadius;
    const centerX = 0; 
    const centerY = 0;

    const angularStep = (2 * Math.PI) / barCount;
    const rotRad = (config.containerRotation || 0) * Math.PI / 180;

    const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
    const targetShape = morph ? morph.to : shape;
    const t = morph ? morph.factor : 0;

    for (let i = 0; i < barCount; i++) {
        const angle = i * angularStep - Math.PI; 
        
        // Get boost for this angle
        const boostIdx = Math.floor(((angle + Math.PI) / (2 * Math.PI)) * 256) % 256;
        const boost = boosts[boostIdx];
        
        const h = minHeight + (boost * (maxHeight - minHeight));
        if (h < 0.1) continue;
        
        // Use ShapeUtils to get the correct radius at this angle, accounting for rotation
        const boundaryRadius = ShapeUtils.getInterpolatedRadiusAtAngle(
            angle - rotRad, 
            shape, 
            targetShape, 
            t, 
            radius, 
            morph?.baseRadius
        );

        const bx = centerX + Math.cos(angle) * boundaryRadius;
        const by = centerY + Math.sin(angle) * boundaryRadius;

        ctx.save();
        ctx.translate(bx, by);
        ctx.rotate(angle);

        const mode = config.rhythmicSpectrumColorMode;
        if (mode === 'RAINBOW') {
            ctx.fillStyle = `hsla(${(globalHue + i * (360 / barCount)) % 360}, 100%, 50%, 0.8)`;
            ctx.shadowColor = `hsla(${(globalHue + i * (360 / barCount)) % 360}, 100%, 50%, 0.4)`;
        } else if (mode === 'RGB_PULSE') {
            const r = Math.floor(127 + 128 * Math.sin(globalHue * 0.05 + i * 0.1));
            const g = Math.floor(127 + 128 * Math.sin(globalHue * 0.05 + i * 0.1 + 2));
            const b = Math.floor(127 + 128 * Math.sin(globalHue * 0.05 + i * 0.1 + 4));
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.8)`;
            ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
        } else if (mode === 'HUE_SHIFT') {
            const hue = (globalHue + boost * 100) % 360;
            ctx.fillStyle = `hsla(${hue}, 100%, 50%, 0.8)`;
            ctx.shadowColor = `hsla(${hue}, 100%, 50%, 0.4)`;
        } else if (mode === 'BALL') {
            ctx.fillStyle = config.ball1Color || '#ffffff';
            ctx.shadowColor = config.ball1Color || '#ffffff';
        } else {
            ctx.fillStyle = config.rhythmicSpectrumCustomColor || '#ffffff';
            ctx.shadowColor = config.rhythmicSpectrumCustomColor || '#ffffff';
        }

        ctx.shadowBlur = 10 * (0.5 + boost);

        // Draw bar growing OUTWARDS (positive X)
        ctx.fillRect(0, -barWidth / 2, h, barWidth);
        
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = 1;
        ctx.strokeRect(0, -barWidth / 2, h, barWidth);
        
        ctx.restore();
    }
  }
};
